import React, { useEffect, useState } from 'react';
import { Gauge, MessageSquare, Sparkles, Users, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/api';

const n = (v) => Math.round(Number(v || 0)).toLocaleString('es-AR');
const short = (v) => v >= 1e6 ? (v / 1e6).toLocaleString('es-AR', { maximumFractionDigits: 1 }) + ' M' : n(v);

// Tokens aproximados de un texto en español (~3,5 caracteres por token)
export const textTokens = (chars) => Math.ceil((chars || 0) / 3.5);

// Estimaciones según el largo de la información del negocio
function estimates(infoChars) {
  const reply = 1050 + textTokens(infoChars) + 130;  // instrucciones + info + formato + respuesta
  return {
    replyStart: reply,
    replyLong: reply + 600,          // con 20 mensajes de historial
    convTypical: 6 * (reply + 250),  // ~6 mensajes del cliente
    convLong: 15 * (reply + 450),    // ~15 mensajes del cliente
    analysis: 1500,
  };
}

function Stat({ icon: Icon, title, value, sub }) {
  return (
    <div style={{ flex: '1 1 160px', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}><Icon size={13} /> {title}</div>
      <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function AiUsageDetail({ infoChars }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get('/usage/ai').then(r => setD(r.data)).catch(() => {}); }, []);

  const est = estimates(infoChars);
  const ai = d?.ai;
  // promedios reales si ya hay suficiente uso; si no, estimados
  const avgReply = ai && ai.bot_replies >= 5 ? ai.bot_tokens / ai.bot_replies : est.replyStart + 250;
  const avgAnalysis = ai && ai.analyses >= 3 ? ai.analysis_tokens / ai.analyses : est.analysis;
  const avgConv = ai && d.bot_conversations >= 3 ? ai.bot_tokens / d.bot_conversations : est.convTypical;
  const remaining = ai ? Math.max(0, ai.limit - ai.used) : 0;
  const color = ai?.blocked ? 'var(--danger)' : ai?.warning ? '#f59e0b' : 'var(--accent)';

  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px', borderBottom: '1px solid var(--border)' };
  const td = { fontSize: 13, padding: '8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
  const rows = [
    ['Una respuesta del bot (inicio de la charla)', 'Instrucciones + información del negocio + el mensaje del cliente', '~' + n(est.replyStart)],
    ['Una respuesta del bot (charla avanzada)', 'Lo mismo + los últimos mensajes de la charla (hasta 20)', '~' + n(est.replyLong)],
    /* La transcripción se cobra por minuto de audio, no por tokens. Se muestra
       convertida a tokens para que se pueda comparar con el resto de la tabla:
       si no, el consumo de audio queda invisible al lado de lo demás. */
    ['Un audio de 1 minuto', 'Se pasa a texto y el bot lo responde como si lo hubieran escrito', '~2.000 + la respuesta'],
    ['Una foto', 'No usa IA: se guarda, el bot avisa y la conversación pasa a un asesor', '0'],
    ['Un sticker', 'No usa IA: el bot responde un texto fijo', '0'],
    ['Una conversación típica (~6 mensajes del cliente)', 'Suma de las respuestas del bot', '~' + n(est.convTypical)],
    ['Una conversación larga (~15 mensajes del cliente)', 'Suma de las respuestas del bot', '~' + n(est.convLong)],
    ['Un análisis IA', 'Los primeros mensajes de la conversación + la conclusión', '~' + n(est.analysis)],
  ];

  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Gauge size={18} color="var(--accent)" />
        <div style={{ fontWeight: 700, fontSize: 15 }}>Consumo del Agente IA</div>
        {d && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{d.renueva && d.period.length > 7 ? `· se renueva el ${d.renueva.split('-').reverse().join('/')}` : `· mes ${d.period.split('-').reverse().join('/')}`}</span>}
      </div>

      {ai && (<>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
          <span><b>{short(ai.used)}</b> de {short(ai.limit)} tokens usados</span>
          <span style={{ color: 'var(--text-muted)' }}>{ai.pct}%</span>
        </div>
        <div style={{ height: 8, background: 'var(--bg-hover)', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ width: ai.pct + '%', height: '100%', background: color }} />
        </div>
        {(ai.warning || ai.blocked) && (
          <div style={{ display: 'flex', gap: 8, fontSize: 13, padding: '8px 10px', borderRadius: 8, marginBottom: 12, background: ai.blocked ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)', color: ai.blocked ? 'var(--danger)' : '#d97706' }}>
            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{ai.blocked ? 'Llegaste al tope de este mes: el agente dejó de responder.' : 'Estás cerca del tope de este mes.'} {d.limit_message}</span>
          </div>
        )}
        {open && (<>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <Stat icon={MessageSquare} title="Respuestas del bot" value={n(ai.bot_replies)} sub={short(ai.bot_tokens) + ' tokens · ~' + n(avgReply) + ' c/u'} />
          <Stat icon={Users} title="Conversaciones atendidas" value={n(d.bot_conversations)} sub={'~' + n(avgConv) + ' tokens c/u'} />
          <Stat icon={Sparkles} title="Análisis IA" value={n(ai.analyses)} sub={short(ai.analysis_tokens) + ' tokens · ~' + n(avgAnalysis) + ' c/u'} />
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          Con lo que te queda ({short(remaining)} tokens) alcanza para aprox. <b>{n(remaining / avgConv)} conversaciones</b> o <b>{n(remaining / avgReply)} respuestas</b> del bot.
        </div>
        </>)}
      </>)}

      {open && (<>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>¿Cuánto consume cada cosa?</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Qué</th><th style={th}>Qué lee la IA</th><th style={{ ...th, textAlign: 'right' }}>Tokens aprox.</th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r[0]}><td style={{ ...td, fontWeight: 600 }}>{r[0]}</td><td style={{ ...td, color: 'var(--text-muted)' }}>{r[1]}</td><td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{r[2]}</td></tr>
          ))}</tbody>
        </table>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 }}>
        La IA no recuerda entre mensajes: en cada respuesta vuelve a leer las instrucciones, la información del negocio y la charla. Por eso <b>lo que más influye es el largo de la información del negocio</b>. Los valores de la tabla se calculan con tu información actual.
      </div>
      </>)}
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(!open)} style={{ marginTop: open ? 12 : 0, gap: 4 }}>
        {open ? <><ChevronUp size={14} /> Ocultar detalle</> : <><ChevronDown size={14} /> Ver detalle del consumo y cuánto consume cada cosa</>}
      </button>
    </div>
  );
}
